/**
 * Sayfa icerigini hafif markdown'a cevirir (yalnizca AI analizi aciksa calisir).
 *
 * Gizlilik kurallari:
 *  - input/textarea/select degerleri ASLA okunmaz (form etiketleri ve linkleri
 *    korunur: urun varyant secenekleri cogunlukla form icinde yasar)
 *  - maskSelectors, pages.removeSelectors, [data-supsis-no-analyze] ve Supsis
 *    widget'i klondan silinir
 *  - navigasyon/altbilgi/kenar cubugu gibi tekrar eden bloklar atilir
 */

const DEFAULT_ROOT_SELECTORS = ["main", "[role=main]", "article", "#content", ".content"];

const STRIP_SELECTORS = [
    "script",
    "style",
    "noscript",
    "svg",
    "iframe",
    "nav",
    "header",
    "footer",
    "aside",
    // form'un kendisi degil, yalnizca kullanici girdisi tasiyan alanlari silinir
    "input",
    "textarea",
    "select",
    "option",
    "[aria-hidden=true]",
    "[data-supsis-no-analyze]",
    "#supsis-chat",
    ".supsis-widget",
    "[id^=supsis]",
];

// Varyant linkleri (renk/beden secenekleri) de buradan gecer
const MAX_LINKS = 80;
const MAX_IMAGES = 5;

function textOf(node) {
    return String((node && node.textContent) || "")
        .replace(/\s+/g, " ")
        .trim();
}

/** Metin yogunlugu en yuksek kok adayini secer. */
function pickRoot(contentSelector) {
    if (contentSelector) {
        try {
            const custom = document.querySelector(contentSelector);
            if (custom) return custom;
        } catch (e) {
            /* gecersiz selector yoksayilir */
        }
    }

    let best = null;
    let bestLength = 0;
    for (let i = 0; i < DEFAULT_ROOT_SELECTORS.length; i++) {
        let nodes;
        try {
            nodes = document.querySelectorAll(DEFAULT_ROOT_SELECTORS[i]);
        } catch (e) {
            continue;
        }
        for (let n = 0; n < nodes.length; n++) {
            const length = textOf(nodes[n]).length;
            if (length > bestLength) {
                bestLength = length;
                best = nodes[n];
            }
        }
    }

    return best || document.body;
}

function stripNodes(root, extraSelectors) {
    const selectors = STRIP_SELECTORS.concat(extraSelectors || []);
    for (let i = 0; i < selectors.length; i++) {
        let nodes;
        try {
            nodes = root.querySelectorAll(selectors[i]);
        } catch (e) {
            continue;
        }
        for (let n = 0; n < nodes.length; n++) {
            if (nodes[n].parentNode) nodes[n].parentNode.removeChild(nodes[n]);
        }
    }
}

function absoluteUrl(href) {
    try {
        return new URL(href, window.location.href).href.slice(0, 300);
    } catch (e) {
        return "";
    }
}

/**
 * "Anahtar / deger" kaliplarini yakalar.
 *
 * Urun ozellik listeleri cogunlukla <li><span>RAM</span><span>16 GB</span></li>
 * ya da <div><span>Renk</span><span>Siyah</span></div> seklindedir. Duz
 * textContent bunlari "RAM16 GB" gibi birlestirdigi icin cift ayri okunur.
 *
 * @returns {Object|null} { k, v }
 */
const PAIR_EXCLUDED_TAGS = ["a", "button", "img", "svg", "video", "label"];

function keyValuePair(node) {
    const children = node.children || [];
    if (children.length !== 2) return null;
    if ((children[0].children || []).length > 0 || (children[1].children || []).length > 0) return null;

    // Buton/link ciftleri (aksiyon satirlari) ozellik degildir; normal akista islensin
    for (let i = 0; i < 2; i++) {
        const childTag = String(children[i].tagName || "").toLowerCase();
        if (PAIR_EXCLUDED_TAGS.indexOf(childTag) !== -1) return null;
    }

    const key = textOf(children[0]);
    const value = textOf(children[1]);
    if (!key || !value) return null;
    if (key.length > 60 || value.length > 160) return null;

    return { k: key, v: value };
}

/** DOM'u satir satir hafif markdown'a cevirir. */
function toMarkdown(root) {
    const lines = [];
    let linkCount = 0;
    let imageCount = 0;

    const walk = function (node) {
        if (!node || !node.tagName) return;
        const tag = node.tagName.toLowerCase();

        if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
            const value = textOf(node);
            if (value) lines.push(new Array(parseInt(tag.slice(1), 10) + 1).join("#") + " " + value);
            return;
        }

        if (tag === "li" || tag === "div") {
            const pair = keyValuePair(node);
            if (pair) {
                lines.push("- " + pair.k + ": " + pair.v);
                return;
            }
        }

        if (tag === "li") {
            const value = textOf(node);
            if (value) lines.push("- " + value.slice(0, 400));
            return;
        }

        // Teknik ozellik listeleri: <dt>RAM</dt><dd>16 GB</dd>
        if (tag === "dl") {
            const children = node.children || [];
            let key = "";
            for (let i = 0; i < children.length; i++) {
                const childTag = String(children[i].tagName || "").toLowerCase();
                const value = textOf(children[i]).slice(0, 200);
                if (childTag === "dt") {
                    key = value;
                } else if (childTag === "dd" && value) {
                    lines.push(key ? "- " + key + ": " + value : "- " + value);
                    key = "";
                }
            }
            return;
        }

        if (tag === "tr") {
            const cells = [];
            const children = node.children || [];
            for (let i = 0; i < children.length; i++) cells.push(textOf(children[i]).slice(0, 120));
            if (cells.length > 0) lines.push("| " + cells.join(" | ") + " |");
            return;
        }

        if (tag === "img" && imageCount < MAX_IMAGES) {
            const alt = String(node.getAttribute("alt") || "").slice(0, 120);
            const src = absoluteUrl(node.getAttribute("src") || "");
            if (src) {
                imageCount++;
                lines.push("![" + alt + "](" + src + ")");
            }
            return;
        }

        if (tag === "a" && linkCount < MAX_LINKS) {
            const label = textOf(node).slice(0, 120);
            const href = absoluteUrl(node.getAttribute("href") || "");
            if (label && href) {
                linkCount++;
                lines.push("[" + label + "](" + href + ")");
                return;
            }
        }

        if (tag === "p" || tag === "blockquote") {
            const value = textOf(node);
            if (value) lines.push(value.slice(0, 1200));
            return;
        }

        const children = node.children || [];
        if (children.length === 0) {
            const value = textOf(node);
            if (value) lines.push(value.slice(0, 600));
            return;
        }
        for (let i = 0; i < children.length; i++) walk(children[i]);
    };

    walk(root);

    // Ust uste gelen ayni satirlari (tekrar eden kart basliklari) tekille
    const output = [];
    let previous = "";
    for (let i = 0; i < lines.length; i++) {
        if (lines[i] === previous) continue;
        previous = lines[i];
        output.push(lines[i]);
    }
    return output.join("\n");
}

/**
 * @param {Object} options { contentSelector, removeSelectors, maskSelectors, maxBytes }
 * @returns {Object|null} { md, len, truncated }
 */
export function extractContent(options) {
    const settings = options || {};
    const maxBytes = settings.maxBytes || 40 * 1024;

    let clone;
    try {
        const root = pickRoot(settings.contentSelector);
        if (!root) return null;
        clone = root.cloneNode(true);
    } catch (e) {
        return null;
    }

    stripNodes(clone, (settings.removeSelectors || []).concat(settings.maskSelectors || []));

    let markdown = toMarkdown(clone).replace(/\n{3,}/g, "\n\n").trim();
    const originalLength = markdown.length;
    const truncated = originalLength > maxBytes;
    if (truncated) markdown = markdown.slice(0, maxBytes);

    if (!markdown) return null;
    return { md: markdown, len: originalLength, truncated: truncated };
}

export default extractContent;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/pages/content.js
// module id = null
// module chunks = 